import { useEffect } from 'react';

import { map } from 'lodash';

import { AlbumFunnelModalService } from '@/components/pages/albums/AlbumFunnelModal/services/AlbumFunnelModalService';

import { useAlbumContext } from '@/utils/hooks/context/useAlbumContext';
import useTranslation from '@/utils/hooks/translation/useTranslation';
import useActiveGroup from '@/utils/hooks/user/useActiveGroup';
import useIsGroup from '@/utils/hooks/user/useIsGroup';

import type { SocialProfileType } from '@/types/socialProfileType';

import { RESTRICTED_NETWORKS } from '@/lib/socialProfile/restrictedNetworks';

import Checkbox from '@/components/base/Checkbox';
import Icon from '@/components/base/Icon';
import Label from '@/components/base/Label';
import { SelectButton } from '@/components/base/Select/SelectComponent';
import { SocialProfilePreview } from '@/components/base/Select/SelectSocialProfile';
import { SelectFilterComponent } from '@/components/base/SelectFilter/SelectFilterComponent';
import Tooltip from '@/components/base/Tooltip';

type SocialProfileOptionType = {
    value: string | number;
    label: string;
    disabled?: boolean;
    socialProfile?: SocialProfileType;
};

type SocialProfilesFilterProps = {
    error?: string;
    onClearError?: () => void;
    showLabel?: boolean;
    className?: string;
};

export const SocialProfilesFilter = ({
    error,
    onClearError,
    showLabel = true,
    className
}: SocialProfilesFilterProps) => {
    const { t } = useTranslation('content');
    const { albumInfo, dispatch } = useAlbumContext();
    const { activeGroup } = useActiveGroup();
    const isGroup = useIsGroup();

    // Filter active social profiles from the group
    const activeSocialProfiles =
        activeGroup?.social_profiles?.filter((profile) => profile.active) || [];

    // For story campaigns, only show Instagram profiles
    const availableProfiles =
        albumInfo?.criteria?.album_type === 'stories'
            ? activeSocialProfiles.filter((profile) => profile.social_network_type === 'instagram')
            : activeSocialProfiles;

    // Separate supported and unsupported profiles, putting unsupported at the bottom
    const supportedProfiles = availableProfiles.filter(
        (profile) => !RESTRICTED_NETWORKS.includes(profile.social_network_type)
    );
    const unsupportedProfiles = availableProfiles.filter((profile) =>
        RESTRICTED_NETWORKS.includes(profile.social_network_type)
    );
    const sortedProfiles = [...supportedProfiles, ...unsupportedProfiles];

    const allOption = {
        id: 'all',
        label: t('content:modal.all_social_profiles'),
        value: 'all',
        disabled: false
    } as const;

    const options = [
        allOption,
        ...sortedProfiles.map((profile) => ({
            label: profile.slug,
            value: profile.id,
            socialProfile: profile,
            disabled: RESTRICTED_NETWORKS.includes(profile.social_network_type)
        }))
    ];

    // Set initial value only when creating a new campaign (not editing)
    // and when no social profiles are already selected
    // and re-initialize when album_type changes (stories/posts toggle)
    useEffect(() => {
        const hasExistingSocialProfiles = albumInfo?.criteria?.social_profiles?.length > 0;
        const isNewCampaign = !albumInfo?.id;

        // Only set all profiles as default when creating new campaign with no selection
        // Exclude unsupported profiles from default selection
        if (isNewCampaign && !hasExistingSocialProfiles) {
            dispatch({ type: 'UPDATE_SOCIAL_PROFILES', payload: map(supportedProfiles, 'id') });
        }
    }, [albumInfo?.criteria?.album_type]);

    // Only show this filter for groups with social profiles
    if (!isGroup || !availableProfiles.length) {
        return null;
    }

    // Get selected options based on current criteria
    const selectedSocialProfileIds = albumInfo?.criteria?.social_profiles || [];
    const profileOptions = options.filter((option) => option.value !== 'all');

    const selectedOptions = profileOptions.filter((option) =>
        selectedSocialProfileIds.includes(option.value)
    );

    // Only count supported profiles for "all selected" check
    const selectedSupportedOptions = selectedOptions.filter((option) => !option.disabled);
    const allAreSelected = selectedSupportedOptions.length === supportedProfiles.length;

    const handleSelectionChange = (newSelectedOptions: SocialProfileOptionType[]) => {
        if (error && onClearError) {
            onClearError();
        }

        const clickedAllOption = newSelectedOptions.find((option) => option.value === 'all');
        const currentSelectedIds = albumInfo?.criteria?.social_profiles ?? [];

        const selectedIds = clickedAllOption
            ? AlbumFunnelModalService.handleAllOptionClick(allAreSelected, supportedProfiles)
            : AlbumFunnelModalService.handleIndividualSelection(
                  newSelectedOptions.at(-1)?.value,
                  currentSelectedIds
              );

        dispatch({ type: 'UPDATE_SOCIAL_PROFILES', payload: selectedIds });
    };

    const renderOption = ({
        option,
        selected
    }: {
        option: SocialProfileOptionType;
        selected: boolean;
    }) => {
        const isSelected = option?.value === 'all' ? allAreSelected : selected;
        const isDisabled = option?.disabled;

        const content = (
            <div
                className={`flex items-center space-x-2 ${
                    isDisabled ? 'cursor-not-allowed opacity-50' : ''
                }`}
                data-testid={`option-select-sp-${option?.value}`}>
                <Checkbox checked={isSelected} readOnly disabled={isDisabled} />

                {option?.value === 'all' ? (
                    <span className="text-sm text-icoBlack dark:text-white">{option?.label}</span>
                ) : (
                    <SocialProfilePreview profile={option?.socialProfile} />
                )}
            </div>
        );

        if (isDisabled) {
            return (
                <Tooltip content={t('content:modal.social_profile_not_supported')}>
                    {content}
                </Tooltip>
            );
        }

        return content;
    };

    const renderButton = ({
        selected: buttonSelected,
        disabled,
        error: buttonError
    }: {
        selected: SocialProfileOptionType[];
        disabled?: boolean;
        error?: string;
    }) => {
        const maxDisplay = 2;
        const totalSelected = buttonSelected?.length || 0;
        const displayedLabels = buttonSelected?.slice(0, maxDisplay).map((option) => option.label);
        const remainingCount = totalSelected - maxDisplay;

        let displayText = t('content:modal.select_social_profiles_placeholder');
        if (totalSelected > 0) {
            displayText =
                remainingCount > 0
                    ? `${displayedLabels.join(', ')} +${remainingCount}`
                    : displayedLabels.join(', ');
        }

        return (
            <SelectButton selected={buttonSelected} disabled={disabled} error={buttonError}>
                <span className="flex items-center space-x-3">
                    {!showLabel && (
                        <Icon
                            icon="single-profile"
                            className="text-base text-icoGray-400 dark:text-icoDarkMode-wolf"
                        />
                    )}
                    <span className="block truncate text-icoBlack dark:text-white">
                        {displayText}
                    </span>
                </span>
            </SelectButton>
        );
    };

    return (
        <div className={className}>
            {showLabel && (
                <Label className="flex items-center space-x-2.5 pb-2" required>
                    <Icon icon="single-profile" className="text-sm text-icoBlue" />
                    <span className="text-icoBlack dark:text-white">
                        {t('content:modal.social_profiles')}
                    </span>
                </Label>
            )}

            <SelectFilterComponent
                selected={selectedOptions}
                setSelected={handleSelectionChange}
                options={options}
                idInput="select-filter-social-profiles"
                placeholder={t('content:modal.select_social_profiles_placeholder')}
                error={error}
                layoutOptions={{ isMultiple: true, noBorder: true, forcedDisplay: true }}
                panelOptions={{
                    optionsWrapperClassName: 'left-0'
                }}
                button={renderButton}>
                {renderOption}
            </SelectFilterComponent>
        </div>
    );
};
